import Vue from 'vue'
import Router from 'vue-router'
import axios from './axios'
import store from './store'

import audioRoute from '@/views/audio/routes'
import committeeRoute from '@/views/committee/routes'
import creatorRoute from '@/views/creator/routes'
import leadershipRoute from '@/views/leadership/routes'
import gurudwaraRoute from '@/views/gurudwara/routes'
import organizationRoute from '@/views/organization/routes'
import mantraRoute from '@/views/mantra/routes'
import bookType2Route from '@/views/book/type-2/routes'
import bookType3Route from '@/views/book/type-3/routes'
import premiumOfferRoute from '@/views/premium-offer/routes'
import restrictedKeywordRoute from '@/views/restricted-keyword/routes'
import monthRoute from '@/views/month/routes'
import bookRoute from '@/views/book/routes'
import playlistRoute from '@/views/playlist/routes'
import roleRoute from '@/views/roles/routes'
import settingRoute from '@/views/setting/routes'
import videoRoute from '@/views/video/routes'
import guideTutorialRoute from '@/views/guide-tutorial/routes'
import feedbackRoute from '@/views/feedback/routes'
import gurudwaraOnboardRoute from '@/views/gurudwara-onboard/routes'
import userRoute from '@/views/user/routes'
import subscriptionPlanRoute from '@/views/subscription-plan/routes'
import adminRoute from '@/views/admin/routes'
import organizationOnboardRoute from '@/views/organization/onboard/routes'
import organizationGuideTutorialRoute from '@/views/organization/guide-tutorial/routes'
import punjabiLetterRoute from '@/views/punjabi-letter/routes'
import searchHistoryRoute from '@/views/search-history/routes'
import gurudwaraServiceRoute from '@/views/services/routes'
import saraiRoute from '@/views/sarai/routes'
import hotelRoute from '@/views/hotel/routes'
import featureFlags from '@/config/featureFlags'
import voiceBySangatRoute from '@/views/voice-by-sangat/routes'
import * as roles from '@/utils/roles'
import { isGurudwaraAdminPortal } from '@/utils/portalGurudwara'


const { ADMIN } = roles

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // MAIN LAYOUT ROUTES
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // Theme Routes
        {
          path: '/',
          name: 'home',
          component: () => import('./views/Home.vue'),
          meta: {
            allowAnonymous: false,
            pageTitle: 'Main Dashboard',
            breadcrumb: [
              { title: 'Dashboard', url: '/' },
              { title: 'Main Dashboard', active: true }
            ]
            // permissions: [ADMIN]
          }
        },
        {
          path: '/sarai-dashboard',
          name: 'sarai-dashboard',
          component: () => import('./views/SaraiDashboard.vue'),
          meta: {
            allowAnonymous: false,
            pageTitle: 'Sarai Dashboard',
            breadcrumb: [
              { title: 'Dashboard', url: '/' },
              { title: 'Sarai Dashboard', active: true }
            ],
            roles: ['superadmin', 'admin']
          }
        },
        {
          path: '/hotel-dashboard',
          name: 'hotel-dashboard',
          component: () => import('./views/hotel/HotelDashboard.vue'),
          meta: {
            allowAnonymous: false,
            pageTitle: 'Hotel Dashboard',
            breadcrumb: [
              { title: 'Dashboard', url: '/' },
              { title: 'Hotel Dashboard', active: true }
            ],
            roles: ['superadmin','admin', 'hotel-manager']
          }
        },
        ...gurudwaraRoute,
        ...audioRoute,
        ...mantraRoute,
        ...bookType2Route,
        ...bookType3Route,
        ...premiumOfferRoute,
        ...restrictedKeywordRoute,
        ...settingRoute,
        ...playlistRoute,
        ...bookRoute,
        ...creatorRoute,
        ...leadershipRoute,
        ...committeeRoute,
        ...roleRoute,
        ...monthRoute,
        ...videoRoute,
        ...guideTutorialRoute,
        ...feedbackRoute,
        ...gurudwaraOnboardRoute,
        ...subscriptionPlanRoute,
        ...adminRoute,
        ...organizationRoute,
        ...userRoute,
        ...organizationOnboardRoute,
        ...organizationGuideTutorialRoute,
        ...punjabiLetterRoute,
        ...searchHistoryRoute,
        ...gurudwaraServiceRoute,
        ...saraiRoute,
        ...hotelRoute,
        ...voiceBySangatRoute
      ]

    },

    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // PAGES
        {
          path: '/login',
          name: 'page-login',
          component: () => import('@/views/pages/Login.vue'),
          meta: {
            allowAnonymous: true
          }
        },
        {
          path: '/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue')
        },
        {
          path: '/forgot-password',
          name: 'ForgotPassword',
          component: () => import('@/views/pages/ForgotPassword.vue')
        },
        {
          path: '/reset-password',
          name: 'ResetPassword',
          component: () => import('@/views/pages/ResetPassword.vue')
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/error-404'
    }
  ]
})

/* ---------------------------------------------------------------------------------------------------------------------------- */
/*                                                    Authenticatation Check                                                    */
/* ---------------------------------------------------------------------------------------------------------------------------- */
const hydrateSession = (payload = {}) => {
  const user = payload.user
  if (!user) return

  store.commit('auth/SET_USER_INFO', user)
  store.commit('auth/SET_STATE', {
    action: 'user',
    data: user
  })
  if (payload.access_permissions) {
    store.commit('auth/SET_PERMISSIONS', payload.access_permissions)
  }
  const roleName = user.role && (user.role.role || user.role.role_name || user.role.slug)
  if (roleName) {
    store.commit('auth/SET_USER_ROLE', roleName)
  }
  if (user.role && user.role.slug) {
    localStorage.setItem('roleSlug', user.role.slug)
  }
  if (payload.hotel) {
    localStorage.setItem('hotel', JSON.stringify(payload.hotel))
  }
  if (payload.gurudwara) {
    localStorage.setItem('gurudwara', JSON.stringify(payload.gurudwara))
    if (payload.gurudwara.square_thumbnail) {
      localStorage.setItem('gurudwaraImage', payload.gurudwara.square_thumbnail)
    }
  }
}

const hasCachedSession = () => {
  const user = store.state.auth.user
  const userInfo = store.state.auth.userInfo
  return !!(user && user._id) || !!(userInfo && userInfo._id)
}

const isAuthenticated = async () => {
  const accessToken = localStorage.getItem('accessToken')
  if (!accessToken) {
    return false
  }
  if (hasCachedSession()) {
    return true
  }
  try {
    const res = await axios.get('/auth/verify-token')
    hydrateSession({
      user: res.data && res.data.data && res.data.data.user,
      access_permissions: res.data && res.data.access_permissions,
      hotel: res.data && res.data.data && res.data.data.hotel,
      gurudwara: res.data && res.data.data && res.data.data.gurudwara
    })
    return !!(res.data && res.data.data && res.data.data.user)
  } catch (err) {
    const status = err && err.response && err.response.status
    // Only a real unauthenticated response should end the session.
    // Network / 5xx / 403 must not bounce every portal role to login.
    if (status === 401) {
      return false
    }
    return true
  }
}

router.beforeEach(async (to, from, next) => {
  if (Object(to.meta).hasOwnProperty('allowAnonymous')) {
    const authed = await isAuthenticated()
    if (!to.meta.allowAnonymous && !authed) {
      localStorage.removeItem('accessToken')
      localStorage.setItem('redirectPath', to.fullPath)
      return next({
        path: '/login'
      })
    } else if (to.meta.allowAnonymous && authed) {
      return next({
        path: '/'
      })
    } else {
      if (to.meta.requiresFeature && !featureFlags[to.meta.requiresFeature]) {
        return next({ path: '/' })
      }
      if (isGurudwaraAdminPortal() && (to.path.startsWith('/hotel') || to.name === 'hotel-dashboard')) {
        return next({ path: '/gurudwara/list' })
      }
      if (!to.meta.allowAnonymous && to.meta && to.meta.permissions && !store.getters['auth/checkPermissionSlug'](to.meta.permissions || [])) {
        const app = window.document.getElementById('app')
        if (app && app.__vue__) {
          app.__vue__.$vs.notify({
            title: 'Error',
            text: `You don't have access to visit "${to.meta.pageTitle}"`,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-center',
            time: 5000,
            color: 'primary'
          })
        }
        return next({
          path: '/'
        })
      } else if (
        !to.meta.requiresAuth &&
        to.meta &&
        to.meta.roles &&
        store.state.auth.userInfo &&
        store.state.auth.userInfo.role &&
        store.state.auth.userInfo.role.slug &&
        !to.meta.roles.includes(store.state.auth.userInfo.role.slug)
      ) {
        const app = window.document.getElementById('app')
        if (app && app.__vue__) {
          app.__vue__.$vs.notify({
            title: 'Error',
            text: `You don't have access to visit "${to.meta.pageTitle}"`,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-center',
            time: 5000,
            color: 'primary'
          })
        }
        return next({
          path: '/'
        })
      }
      return next()
    }
  }
  return next()
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
